import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
import LocationSelector from '../LocationSelector';
import Button from '../../elements/Button';
import Column from '../../elements/Col';
import Text from '../../elements/Text';
import Box from '../../elements/Box';
import Icon from '../../elements/Icon';
import AirportIataCode from '../../containers/_components/AirportIataCode';
import {
  setSelectedOrigin,
  useStore,
  setChangedOrigin
} from './../../containers/InteractiveMapContainer/hooks/useIMCState';

export default function OriginDestinationFilter({
  origin,
  destination,
  showOrigin,
  showDestination,
  showSwitch,
  showAirportIataCode,
  components,
  swapLocations,
  showLabels,
  placeholderOriginKey,
  placeholderDestinationKey,
  isDirty,
  setWasClear,
  setIsClearOriginFocus,
  setIsClearDestinationFocus,
  isClearOriginFocus,
  isClearDestinationFocus,
  setEnableAriaHiddenFooter,
  setCloseButtonFocus,
  closeButtonFocus,
  secondary,
  forcedLoading = false,
  translateEmptyOrNotAvailable
}) {
  const { translate } = useContext(Context);
  const {
    origin: originSettings,
    destination: destinationSettings,
    column: columnSettings,
    switch: switchSettings = {}
  } = components;

  const setSelectedOriginFn = useStore(setSelectedOrigin);
  const setChangedOriginFn = useStore(setChangedOrigin);
  function renderItem(item, locationProps) {
    return (
      <AirportIataCode
        item={item}
        showAirportIataCode={showAirportIataCode}
        {...locationProps.listItem}
      />
    );
  }
  const renderOriginLocationSelector = (
    <>
      <LocationSelector
        flex="1"
        paddingRight={showDestination && !showSwitch ? '2' : undefined}
        showLabel={showLabels}
        {...originSettings}
        defaultValue={secondary ? origin.valueSelect : origin.value}
        options={
          !translateEmptyOrNotAvailable
            ? origin.data
            : origin.data.map((obj) => {
                if (obj.label.includes('empty')) {
                  obj.label = translate('cmp-origin-destination-empty', 'Airport Name is Empty');
                }
                if (obj.label.includes('not-available')) {
                  obj.label = translate(
                    `cmp-origin-destination-not-available`,
                    'Airport is not available'
                  );
                }
                return obj;
              })
        }
        loading={origin.isLoading}
        onChange={(e) => {
          //TODO: This fix was necessary for allow to set on the map global state the origin data, this is only use on the map container
          const newNotifyChange =
            originSettings.notifyChange !== undefined && originSettings.notifyChange !== null
              ? originSettings.notifyChange
              : false;
          secondary ? origin.selectOrigin(e) : origin.onChange(e);
          if (newNotifyChange) {
            setSelectedOriginFn(e);
            setChangedOriginFn(true);
          }
        }}
        onClear={() => {
          secondary ? origin.selectOrigin({ value: '' }) : origin.onClear();
        }}
        itemRenderer={renderItem}
        dataTestId="locationSelector-origin"
        placeholder={translate(placeholderOriginKey, 'Select Departure City')}
        isDirty={isDirty}
        onBlur={() => {
          if (isDirty) {
            setWasClear(false);
          }
        }}
        setIsClearOriginFocus={setIsClearOriginFocus}
        setIsClearDestinationFocus={setIsClearDestinationFocus}
        isClearOriginFocus={isClearOriginFocus}
        isClearDestinationFocus={isClearDestinationFocus}
        setEnableAriaHiddenFooter={setEnableAriaHiddenFooter}
        setCloseButtonFocus={setCloseButtonFocus}
        closeButtonFocus={closeButtonFocus}
        name="Origin"
        notifySelectedState={false}
      />
      {(secondary
        ? origin.valueSelect === ''
        : origin.value === '' || secondary
        ? origin.valueSelect === undefined
        : origin.value === undefined) &&
        originSettings &&
        (originSettings.showEmptyInputLabel ?? false) && (
          <Text {...originSettings.emptyInputLabelSettings}>
            {translate('cmp-origin-empty-input-label', 'Input origin')}
          </Text>
        )}
    </>
  );

  const renderDestinationLocationSelector = (
    <>
      <LocationSelector
        flex="1"
        showLabel={showLabels}
        {...destinationSettings}
        defaultValue={secondary ? destination.valueSelect : destination.value}
        options={
          !translateEmptyOrNotAvailable
            ? destination.data
            : destination.data.map((obj) => {
                if (obj.label.includes('empty')) {
                  obj.label = translate('cmp-origin-destination-empty', 'Airport Name is Empty');
                }
                if (obj.label.includes('not-available')) {
                  obj.label = translate(
                    `cmp-origin-destination-not-available`,
                    'Airport is not available'
                  );
                }
                return obj;
              })
        }
        loading={destination.isLoading || forcedLoading}
        onChange={secondary ? destination.selectDestination : destination.onChange}
        onClear={() => {
          secondary ? destination.selectDestination({ value: '' }) : destination.onClear();
        }}
        itemRenderer={renderItem}
        dataTestId="locationSelector-destination"
        placeholder={translate(placeholderDestinationKey, 'Select Destination City')}
        isDirty={isDirty}
        onBlur={() => {
          if (isDirty) {
            setWasClear(false);
          }
        }}
        name="Destination"
        labelKey="destination"
        setIsClearDestinationFocus={setIsClearDestinationFocus}
        setIsClearOriginFocus={setIsClearOriginFocus}
        isClearOriginFocus={isClearOriginFocus}
        isClearDestinationFocus={isClearDestinationFocus}
        setEnableAriaHiddenFooter={setEnableAriaHiddenFooter}
        setCloseButtonFocus={setCloseButtonFocus}
        closeButtonFocus={closeButtonFocus}
        notifySelectedState={false}
      />
      {(secondary
        ? destination.valueSelect === ''
        : destination.value === '' || secondary
        ? destination.valueSelect === undefined
        : destination.value === undefined) &&
        destinationSettings &&
        (destinationSettings.showEmptyInputLabel ?? false) && (
          <Text {...destinationSettings.emptyInputLabelSettings}>
            {translate('cmp-destination-empty-input-label', 'Input destination')}
          </Text>
        )}
    </>
  );

  return (
    <Column display="flex" {...columnSettings}>
      {showOrigin && (
        <>
          {originSettings && (originSettings.showEmptyInputLabel ?? false) ? (
            <Box {...originSettings.originContainerSetting}>{renderOriginLocationSelector}</Box>
          ) : (
            renderOriginLocationSelector
          )}
        </>
      )}
      {showSwitch && showDestination && (
        <Button
          disabled={
            !(secondary ? destination.valueSelect : destination.value) ||
            !(secondary ? origin.valueSelect : origin.value)
          }
          {...switchSettings}
          ariaLabel={translate('cmp-switch-origin-destination-aria-label')}
          className={`${switchSettings.className} fr-switchOriginDestination fr-d-none fr-d-lg-block`}
          onClick={swapLocations}
          dataTestId="locationSelectorBtnSwitch"
        >
          <Icon
            icon="switch-horizontal"
            {...(switchSettings.iconSettings ? switchSettings.iconSettings : {})}
          />
        </Button>
      )}
      {showDestination && (
        <>
          {destinationSettings && (destinationSettings.showEmptyInputLabel ?? false) ? (
            <Box {...destinationSettings.destinationContainerSetting}>
              {renderDestinationLocationSelector}
            </Box>
          ) : (
            renderDestinationLocationSelector
          )}
        </>
      )}
    </Column>
  );
}

OriginDestinationFilter.propTypes = {
  showOrigin: PropTypes.bool,
  showDestination: PropTypes.bool,
  showSwitch: PropTypes.bool,
  showAirportIataCode: PropTypes.bool,
  showLabels: PropTypes.bool,
  components: PropTypes.shape({
    origin: PropTypes.objectOf(PropTypes.any),
    destination: PropTypes.objectOf(PropTypes.any),
    column: PropTypes.objectOf(PropTypes.any),
    switch: PropTypes.objectOf(PropTypes.any)
  }),
  origin: PropTypes.shape({
    value: PropTypes.string,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.any)
  }),
  destination: PropTypes.shape({
    value: PropTypes.string,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.any)
  }),
  swapLocations: PropTypes.func,
  placeholderOriginKey: PropTypes.string,
  placeholderDestinationKey: PropTypes.string,
  secondary: PropTypes.bool,
  translateEmptyOrNotAvailable: PropTypes.bool
};

OriginDestinationFilter.defaultProps = {
  showOrigin: true,
  showDestination: false,
  showSwitch: false,
  showAirportIataCode: false,
  showLabels: false,
  components: {},
  origin: {
    value: PropTypes.string,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.any)
  },
  destination: {
    value: PropTypes.string,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.any)
  },
  swapLocations: PropTypes.func,
  placeholderOriginKey: 'cmp-location-origin-placeholder',
  placeholderDestinationKey: 'cmp-location-destination-placeholder',
  secondary: false,
  translateEmptyOrNotAvailable: false
};
